import {faChevronLeft, faChevronRight} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useRef, useEffect} from 'react';

type ImageCarouselProps = {
  images: string[];
  title: string;
};

function ImageCarousel({images, title}: ImageCarouselProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const extendedImages =
    images.length > 1
      ? [images[images.length - 1], ...images, images[0]]
      : images;

  // Initialize scroll to the first real image (sub-index 1)
  useEffect(() => {
    if (images.length > 1 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      // Wait for layout to be sure about widths
      requestAnimationFrame(() => {
        if (!container) return;
        const itemWidth = container.clientWidth;
        const gap = 16; // gap-4
        const stride = itemWidth + gap;
        // Jump to index 1
        container.scrollLeft = stride;
      });
    }
  }, [images]); // Reset when images change

  const handleScroll = () => {
    if (images.length <= 1 || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;

    const {scrollLeft} = container;
    const itemWidth = container.clientWidth;
    const gap = 16;
    const stride = itemWidth + gap;

    const totalRealWidth = images.length * stride;

    // Detect if we've scrolled into the 'start clone' (Left side)
    // The start clone is at index 0. Real content starts at index 1 (offset = 1 * stride)
    // If scrollLeft is close to 0, we are at the clone.
    if (scrollLeft <= gap) {
      // Allow small buffer
      // Teleport to the real last item (index N)
      // Position = N * stride
      container.scrollLeft = totalRealWidth + scrollLeft;
    }
    // Detect if we've scrolled into the 'end clone' (Right side)
    // The end clone is at index N + 1.
    // Position starts at (N + 1) * stride.
    else if (scrollLeft >= (images.length + 1) * stride - gap) {
      // Teleport to the real first item (index 1)
      // Position = 1 * stride
      container.scrollLeft = scrollLeft - totalRealWidth;
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const itemWidth = scrollContainerRef.current.clientWidth;
      const gap = 16;
      const stride = itemWidth + gap;

      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -stride : stride,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className='relative group'>
      <button
        onClick={() => scroll('left')}
        className='flex absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-all -ml-4'
        aria-label='Previous image'
        type='button'
      >
        <FontAwesomeIcon icon={faChevronLeft} className='w-4 h-4' />
      </button>

      <button
        onClick={() => scroll('right')}
        className='flex absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-all -mr-4'
        aria-label='Next image'
        type='button'
      >
        <FontAwesomeIcon icon={faChevronRight} className='w-4 h-4' />
      </button>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className='w-full overflow-x-auto snap-x snap-mandatory flex gap-4 pb-4 no-scrollbar items-center'
      >
        {extendedImages.map((img, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`${img}-${i}`}
            role='img'
            aria-label={`${title} - ${i + 1}`}
            className='w-full shrink-0 snap-center aspect-square bg-cover bg-center bg-no-repeat'
            style={{
              backgroundImage: `url('${img}')`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default ImageCarousel;
